// Object type definitions for the pitch editor.
// Each type describes visual (color/shape) + default label, used both by the
// toolbar and the SVG pitch renderer.

export const OBJECT_TYPES = {
  player: {
    key: "player",
    name: "Player",
    shortLabel: "P",
    fill: "#0F172A",
    text: "#FFFFFF",
    shape: "circle",
    size: 26,
    icon: "User",
  },
  defender: {
    key: "defender",
    name: "Defender",
    shortLabel: "D",
    fill: "#DC2626",
    text: "#FFFFFF",
    shape: "circle",
    size: 26,
    icon: "Shield",
  },
  neutral: {
    key: "neutral",
    name: "Neutral",
    shortLabel: "N",
    fill: "#F59E0B",
    text: "#0F172A",
    shape: "circle",
    size: 26,
    icon: "Circle",
  },
  goalkeeper: {
    key: "goalkeeper",
    name: "Goalkeeper",
    shortLabel: "GK",
    fill: "#10B981",
    text: "#0F172A",
    shape: "circle",
    size: 26,
    icon: "Hand",
  },
  ball: {
    key: "ball",
    name: "Ball",
    shortLabel: "",
    fill: "#FFFFFF",
    text: "#0F172A",
    shape: "ball",
    size: 14,
    icon: "CircleDot",
  },
  cone: {
    key: "cone",
    name: "Cone",
    shortLabel: "",
    fill: "#F97316",
    text: "#FFFFFF",
    shape: "triangle",
    size: 13,
    icon: "Triangle",
  },
  goal: {
    key: "goal",
    name: "Goal",
    shortLabel: "",
    fill: "#0F172A",
    text: "#FFFFFF",
    shape: "goal",
    size: 40,
    icon: "Goal",
  },
  miniGoal: {
    key: "miniGoal",
    name: "Mini Goal",
    shortLabel: "",
    fill: "#0F172A",
    text: "#FFFFFF",
    // Renders through the exact same `shape: "goal"` branch as the full
    // goal (same visual design). Half the default `size` produces a goal
    // that is exactly 50% of the full Goal's default width because the
    // rendered rect width = 2 × (size × sizeScale).
    shape: "goal",
    size: 20,
    icon: "MiniGoal",
  },
  mannequin: {
    key: "mannequin",
    name: "Mannequin",
    shortLabel: "",
    fill: "#FACC15",
    text: "#0F172A",
    shape: "mannequin",
    size: 18,
    icon: "Accessibility",
  },
  coach: {
    key: "coach",
    name: "Coach",
    shortLabel: "C",
    // Uses the exact same visual language as the other player-like
    // objects (circle + centered label). Default is a black circle with
    // a white "C" — see isPlayerLikeType() below.
    fill: "#0F172A",
    text: "#FFFFFF",
    shape: "circle",
    size: 26,
    icon: "CoachLetter",
  },
};

export const TOOLBAR_ORDER = [
  "player",
  "defender",
  "neutral",
  "goalkeeper",
  "coach",
  "ball",
  "cone",
  "goal",
  "miniGoal",
  "mannequin",
];

// Canonical pitch coordinate system.
// Every activity stores object/shape/arrow/line/text positions in this
// single coordinate space. Views describe a viewport (a canonical
// rectangle) that is projected to the visible SVG — switching views
// never mutates stored coordinates.
export const CANONICAL_PITCH = { w: 680, h: 1050 };

// Pitch orientation + viewport dimensions.
//   - `vertical` controls upright vs. rotated 90° rendering.
//   - `viewport` describes the canonical rectangle currently visible.
//     `x, y` = top-left in canonical coords, `w, h` = size in canonical.
//   - `half` is retained purely so downstream helpers that check for
//     "half-length view" keep working; it is derived from viewport size.
// Top/Bottom refer to the CANONICAL pitch — top-half is the half nearer
// canonical y=0 regardless of whether the pitch is currently drawn
// vertically or rotated horizontally.
export const PITCH_MODES = {
  "vertical-full":       { label: "Vertical · Full",             vertical: true,  half: false, viewport: { x: 0, y: 0,   w: 680, h: 1050 } },
  "vertical-top":        { label: "Vertical · Top Half",         vertical: true,  half: true,  viewport: { x: 0, y: 0,   w: 680, h: 525  } },
  "vertical-bottom":     { label: "Vertical · Bottom Half",      vertical: true,  half: true,  viewport: { x: 0, y: 525, w: 680, h: 525  } },
  "vertical-attacking":  { label: "Vertical · Attacking Third",  vertical: true,  half: false, viewport: { x: 0, y: 0,   w: 680, h: 350  } },
  "vertical-middle":     { label: "Vertical · Middle Third",     vertical: true,  half: false, viewport: { x: 0, y: 350, w: 680, h: 350  } },
  "vertical-defensive":  { label: "Vertical · Defensive Third",  vertical: true,  half: false, viewport: { x: 0, y: 700, w: 680, h: 350  } },
  "horizontal-full":     { label: "Horizontal · Full",           vertical: false, half: false, viewport: { x: 0, y: 0,   w: 680, h: 1050 } },
  "horizontal-top":      { label: "Horizontal · Top Half",       vertical: false, half: true,  viewport: { x: 0, y: 0,   w: 680, h: 525  } },
  "horizontal-bottom":   { label: "Horizontal · Bottom Half",    vertical: false, half: true,  viewport: { x: 0, y: 525, w: 680, h: 525  } },
  "horizontal-attacking":{ label: "Horizontal · Attacking Third", vertical: false, half: false, viewport: { x: 0, y: 0,   w: 680, h: 350  } },
  "horizontal-middle":   { label: "Horizontal · Middle Third",    vertical: false, half: false, viewport: { x: 0, y: 350, w: 680, h: 350  } },
  "horizontal-defensive":{ label: "Horizontal · Defensive Third", vertical: false, half: false, viewport: { x: 0, y: 700, w: 680, h: 350  } },
};

// Field-colour presets used by the "Field Color" control. Each preset
// bundles the pitch surface colour with an appropriate markings colour so
// standard lines and dots stay visible on both dark and light surfaces.
export const FIELD_COLORS = [
  { key: "standard", label: "Standard Green", fill: "#86BC5A", markings: "#FFFFFF" },
  { key: "dark",     label: "Dark Green",     fill: "#2F5D3A", markings: "#FFFFFF" },
  { key: "light",    label: "Light Green",    fill: "#CBE7A6", markings: "#0F172A" },
  { key: "blue",     label: "Blue",           fill: "#3B4B8F", markings: "#FFFFFF" },
  { key: "grey",     label: "Grey",           fill: "#8A99A6", markings: "#FFFFFF" },
  { key: "white",    label: "White",          fill: "#F5F5F5", markings: "#0F172A" },
];

export const DEFAULT_FIELD_COLOR = FIELD_COLORS[0].fill;

export const getFieldPreset = (fill) =>
  FIELD_COLORS.find((c) => c.fill.toLowerCase() === (fill || "").toLowerCase()) || FIELD_COLORS[0];

// Legacy pitchMode values from before Top/Bottom split. Map them to the
// closest current mode so saved sessions still open correctly.
export const LEGACY_PITCH_MODE_ALIAS = {
  "vertical-half": "vertical-bottom",
  "horizontal-half": "horizontal-bottom",
};


// ---------------------------------------------------------------
// Player size preset system
// ---------------------------------------------------------------
// The Activity Editor owns player sizing via a three-level preset:
// Small / Medium / Large. Each preset defines a single `scale` that
// multiplies the natural per-type radius (see OBJECT_TYPES.*.size).
// Because PitchObject derives BOTH the circle radius AND the label
// font size from that scale (fontSize = s × 0.55/0.7/0.85), the label
// automatically scales in proportion — a label can never grow
// disproportionately large relative to its player icon.
//
// The saved player object stores `size: "small" | "medium" | "large"`.
// Numeric legacy values (or missing values) coerce to "medium" so
// pre-existing activities load unchanged.
export const PLAYER_SIZES = {
  small:  { key: "small",  label: "Small",  scale: 0.38 },
  medium: { key: "medium", label: "Medium", scale: 0.55 },
  large:  { key: "large",  label: "Large",  scale: 0.80 },
};

export const PLAYER_SIZE_KEYS = ["small", "medium", "large"];
export const DEFAULT_PLAYER_SIZE = "medium";

// Player-like object types share the size preset system + selection UI.
// "coach" is included here so the whistle-icon Coach object automatically
// gets S/M/L presets, drag-resize, group scaling and label editing without
// duplicating any of that logic.
const PLAYER_LIKE_TYPES = new Set(["player", "defender", "neutral", "goalkeeper", "coach"]);
export const isPlayerLikeType = (type) => PLAYER_LIKE_TYPES.has(type);

// Coerce any incoming `size` value (string preset, numeric legacy or
// missing) to a canonical preset key. Used at load time so downstream
// code only ever sees "small" | "medium" | "large" for player-likes.
export const resolvePlayerSizeKey = (raw) => {
  if (typeof raw === "string" && PLAYER_SIZES[raw]) return raw;
  return DEFAULT_PLAYER_SIZE;
};

// Convert a player's stored `size` into the numeric scale factor
// PitchObject uses to compute its rendered circle radius and label
// font size. Accepts either:
//   • a preset key string ("small" | "medium" | "large") — from the
//     S/M/L buttons in the InfoPanel, or
//   • a numeric scale in [0.2, 4] — set by the per-player resize
//     handle on the pitch. Numeric values pass through untouched so
//     the same value drives both the circle radius and the label
//     font size (single source of truth).
// Falls back to the "medium" preset for missing/invalid values so
// legacy activities remain visually stable.
export const resolvePlayerScale = (raw) => {
  if (typeof raw === "number" && Number.isFinite(raw) && raw >= 0.2 && raw <= 4) {
    return raw;
  }
  return PLAYER_SIZES[resolvePlayerSizeKey(raw)].scale;
};

// Normalise an incoming player-like object so its `size` is either a
// preset key string or a numeric scale in the valid range. Non-player
// objects are returned untouched.
export const normalizePlayerSize = (obj) => {
  if (!obj || !isPlayerLikeType(obj.type)) return obj;
  const sz = obj.size;
  // Preserve valid per-player numeric scales set via the resize handle.
  if (typeof sz === "number" && Number.isFinite(sz) && sz >= 0.2 && sz <= 4) {
    return obj;
  }
  return { ...obj, size: resolvePlayerSizeKey(sz) };
};
